import React, { useState, useEffect } from 'react';
import { adminAPI } from '@/services/api';
import { motion } from 'framer-motion';
import { 
  Bell,
  Send,
  Users,
  User,
  Search
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.3 }
};

export default function AdminNotificationsPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    user_id: '',
    send_to_all: false
  });
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await adminAPI.getUsers();
      setUsers(response.data.users.filter(u => u.status === 'active'));
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (!formData.title || !formData.message) {
      toast.error('Please enter title and message');
      return;
    }

    if (!formData.send_to_all && !formData.user_id) {
      toast.error('Please select a user or enable "Send to All"');
      return;
    }

    setSending(true);
    try {
      await adminAPI.sendNotification(formData);
      toast.success(formData.send_to_all 
        ? 'Notification sent to all users' 
        : 'Notification sent to user'
      );
      setFormData({ title: '', message: '', user_id: '', send_to_all: false });
    } catch (error) {
      toast.error('Failed to send notification');
    } finally {
      setSending(false);
    }
  };

  const filteredUsers = users.filter(user => 
    user.name?.toLowerCase().includes(search.toLowerCase()) ||
    user.email?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-hotpink-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <motion.div 
      initial="initial"
      animate="animate"
      className="space-y-6"
      data-testid="admin-notifications-page"
    >
      {/* Header */}
      <motion.div variants={fadeInUp}>
        <h1 className="text-3xl font-outfit font-bold text-white">Send Notifications</h1>
        <p className="text-white/60 mt-1">Send notifications to users</p>
      </motion.div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Compose Form */}
        <motion.div variants={fadeInUp} className="glass-card p-6">
          <h2 className="text-xl font-outfit font-semibold text-white mb-4 flex items-center gap-2">
            <Bell className="w-5 h-5 text-cyan-400" />
            Compose Notification
          </h2>
          
          <div className="space-y-4">
            {/* Send to All Toggle */}
            <div className="flex items-center justify-between p-4 rounded-lg bg-white/5">
              <div className="flex items-center gap-3">
                <Users className="w-5 h-5 text-cyan-400" />
                <div>
                  <p className="text-white font-medium">Send to All Users</p>
                  <p className="text-white/50 text-sm">Broadcast to all active users</p>
                </div>
              </div>
              <Switch
                checked={formData.send_to_all}
                onCheckedChange={(checked) => setFormData({ ...formData, send_to_all: checked, user_id: '' })}
                data-testid="send-to-all-switch"
              />
            </div>

            {/* User Selection (if not sending to all) */}
            {!formData.send_to_all && (
              <div className="space-y-2">
                <Label className="text-white/80">Select User</Label>
                <Select 
                  value={formData.user_id} 
                  onValueChange={(v) => setFormData({ ...formData, user_id: v })}
                >
                  <SelectTrigger className="input-field" data-testid="user-select">
                    <User className="w-4 h-4 mr-2 text-white/50" />
                    <SelectValue placeholder="Select a user" />
                  </SelectTrigger>
                  <SelectContent className="bg-space-100 border-white/10 max-h-64">
                    {filteredUsers.map((user) => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.name} ({user.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Title */}
            <div className="space-y-2">
              <Label className="text-white/80">Title</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Notification title"
                className="input-field"
                data-testid="notification-title-input"
              />
            </div>

            {/* Message */}
            <div className="space-y-2">
              <Label className="text-white/80">Message</Label>
              <Textarea
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                placeholder="Enter your notification message..."
                className="input-field min-h-32"
                data-testid="notification-message-input"
              />
            </div>

            {/* Send Button */}
            <Button
              onClick={handleSend}
              className="btn-primary w-full"
              disabled={sending}
              data-testid="send-notification-btn"
            >
              {sending ? (
                <>
                  <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin mr-2" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Notification
                </>
              )}
            </Button>
          </div>
        </motion.div>

        {/* Quick Templates */}
        <motion.div variants={fadeInUp} className="glass-card p-6">
          <h2 className="text-xl font-outfit font-semibold text-white mb-4">Quick Templates</h2>
          <div className="space-y-3">
            {[
              { title: 'Welcome', message: 'Welcome to Dexfolio! Start your investment journey today.' },
              { title: 'Deposit Reminder', message: 'Don\'t forget to make a deposit to start earning daily profits!' },
              { title: 'Profit Distribution', message: 'Daily profits have been distributed to your account. Check your balance!' },
              { title: 'Maintenance Notice', message: 'Scheduled maintenance will occur tonight. Thank you for your patience.' },
              { title: 'Security Alert', message: 'Please update your password to keep your account secure.' },
            ].map((template, index) => (
              <button
                key={index}
                onClick={() => setFormData({ ...formData, title: template.title, message: template.message })}
                className="w-full text-left p-4 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
              >
                <p className="text-white font-medium">{template.title}</p>
                <p className="text-white/50 text-sm truncate">{template.message}</p>
              </button>
            ))}
          </div>
        </motion.div>
      </div>

      {/* User Stats */}
      <motion.div variants={fadeInUp} className="glass-card p-6">
        <h2 className="text-xl font-outfit font-semibold text-white mb-4">Active Users ({users.length})</h2>
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
          <Input
            placeholder="Search users..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input-field pl-10"
          />
        </div>
        <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 max-h-64 overflow-y-auto">
          {filteredUsers.map((user) => (
            <button
              key={user.id}
              onClick={() => setFormData({ ...formData, user_id: user.id, send_to_all: false })}
              className={`p-3 rounded-lg border transition-colors text-left ${
                formData.user_id === user.id 
                  ? 'border-cyan-500 bg-cyan-500/10' 
                  : 'border-white/10 hover:border-white/30 bg-white/5'
              }`}
            >
              <p className="text-white text-sm font-medium truncate">{user.name}</p>
              <p className="text-white/50 text-xs truncate">{user.email}</p>
            </button>
          ))}
        </div>
      </motion.div>
    </motion.div>
  );
}
